import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminAxios, setAdminToken, getAdminToken } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { LogOut, Plus, Sparkles, Trash2, Pencil, RefreshCw } from "lucide-react";

const BUSINESS_TYPES = ["hotel", "restaurant", "diving", "shop", "activity"];
const LANGS = ["en", "de", "fr", "it", "ru", "ar", "zh", "ja", "da"];
const SECTIONS = ["intro", "history", "sights", "beaches", "diving", "mountains", "culture", "tips"];

// ============================================================
// Reusable image input — "Foto hochladen" button + URL field + preview
// Use this anywhere an image URL is stored. Works for hero images,
// business listings, sight highlights, home feature cards, etc.
// ============================================================
function ImageUploadInput({ value, onChange, testId = "img", previewClassName = "w-full h-40 object-cover", placeholder = "https://…  oder  /api/uploads/…" }) {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 15 MB)");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await adminAxios().post(`/admin/upload-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = r?.data?.url;
      if (url) {
        onChange(url);
        toast.success("Bild hochgeladen");
      } else {
        toast.error("Upload fehlgeschlagen");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <label
          className="inline-flex items-center gap-2 cursor-pointer rounded-md border border-stone-300 bg-stone-50 hover:bg-stone-100 px-3 py-2 text-sm"
          data-testid={`${testId}-upload-btn`}
        >
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
            data-testid={`${testId}-file`}
          />
          {uploading ? "Lädt hoch…" : "Foto hochladen"}
        </label>
        <span className="text-xs text-stone-500">oder URL einfügen:</span>
      </div>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
      />
      {value && (
        <div className="rounded-lg overflow-hidden border border-stone-200 bg-stone-100">
          <img src={value} alt="Preview" className={previewClassName} />
        </div>
      )}
    </div>
  );
}

const EMPTY = {
  name: "", type: "hotel", description: "", address: "", phone: "", email: "", website: "",
  image_url: "", rating: 0, featured: false, sponsored: false, tags: [],
};

export default function AdminPage() {
  const nav = useNavigate();
  const { t } = useI18n();
  const [businesses, setBusinesses] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [contentStatus, setContentStatus] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [genLangs, setGenLangs] = useState(["en", "de"]);
  const [overwrite, setOverwrite] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!getAdminToken()) {
      nav("/admin/login");
      return;
    }
    refresh();
  }, []); // eslint-disable-line

  const refresh = async () => {
    const ax = adminAxios();
    try {
      const [b, i, c] = await Promise.all([
        ax.get("/businesses"),
        ax.get("/admin/inquiries"),
        ax.get("/admin/content/status"),
      ]);
      setBusinesses(b.data);
      setInquiries(i.data);
      setContentStatus(c.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAdminToken(null);
        nav("/admin/login");
      }
    }
  };

  const logout = () => { setAdminToken(null); nav("/admin/login"); };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b.id);
    setForm({ ...EMPTY, ...b, tags: b.tags || [] });
    setOpen(true);
  };

  const saveBusiness = async () => {
    const ax = adminAxios();
    try {
      const payload = { ...form, rating: parseFloat(form.rating) || 0 };
      if (editing) {
        await ax.put(`/admin/businesses/${editing}`, payload);
        toast.success("Updated");
      } else {
        await ax.post(`/admin/businesses`, payload);
        toast.success("Created");
      }
      setOpen(false);
      refresh();
    } catch {
      toast.error("Save failed");
    }
  };

  const deleteBusiness = async (id) => {
    if (!confirm("Delete this listing?")) return;
    await adminAxios().delete(`/admin/businesses/${id}`);
    toast.success("Deleted");
    refresh();
  };

  const toggleFlag = async (b, flag) => {
    await adminAxios().put(`/admin/businesses/${b.id}`, { [flag]: !b[flag] });
    refresh();
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await adminAxios().post(`/admin/content/generate`, { langs: genLangs, overwrite });
      toast.success(`Generated ${res.data.generated.length}, skipped ${res.data.skipped.length}, failed ${res.data.failed.length}`);
      refresh();
    } catch {
      toast.error("Generation failed");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <main className="min-h-screen bg-stone-50" data-testid="admin-page">
      <div className="border-b border-stone-200 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <h1 className="font-display text-xl">{t("admin_dashboard")}</h1>
          <Button variant="outline" onClick={logout} data-testid="admin-logout"><LogOut className="h-4 w-4 me-2" /> {t("admin_logout")}</Button>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        <Tabs defaultValue="businesses">
          <TabsList>
            <TabsTrigger value="businesses" data-testid="tab-admin-businesses">{t("admin_businesses")}</TabsTrigger>
            <TabsTrigger value="inquiries" data-testid="tab-admin-inquiries">{t("admin_inquiries")}</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-admin-content">{t("admin_content")}</TabsTrigger>
          </TabsList>

          <TabsContent value="businesses" className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl">Listings</h2>
              <div className="flex gap-2">
                <Button variant="outline" onClick={refresh}><RefreshCw className="h-4 w-4 me-2" />Refresh</Button>
                <Button onClick={openNew} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)]" data-testid="add-business-btn"><Plus className="h-4 w-4 me-2" />New listing</Button>
              </div>
            </div>

            <div className="rounded-2xl border border-stone-200 bg-white overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Featured</TableHead>
                    <TableHead>Sponsored</TableHead>
                    <TableHead>Rating</TableHead>
                    <TableHead className="text-end">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {businesses.map((b) => (
                    <TableRow key={b.id} data-testid={`row-${b.id}`}>
                      <TableCell className="font-medium">{b.name}</TableCell>
                      <TableCell><Badge variant="outline">{b.type}</Badge></TableCell>
                      <TableCell>
                        <Switch checked={b.featured} onCheckedChange={() => toggleFlag(b, "featured")} data-testid={`featured-${b.id}`} />
                      </TableCell>
                      <TableCell>
                        <Switch checked={b.sponsored} onCheckedChange={() => toggleFlag(b, "sponsored")} data-testid={`sponsored-${b.id}`} />
                      </TableCell>
                      <TableCell>{b.rating?.toFixed?.(1) ?? b.rating}</TableCell>
                      <TableCell className="text-end space-x-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(b)} data-testid={`edit-${b.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                        <Button size="sm" variant="outline" onClick={() => deleteBusiness(b.id)} data-testid={`delete-${b.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          <TabsContent value="inquiries" className="mt-8">
            <h2 className="font-display text-2xl mb-4">Advertise Inquiries</h2>
            <div className="space-y-3">
              {inquiries.length === 0 && <p className="text-stone-500">No inquiries yet.</p>}
              {inquiries.map((q) => (
                <div key={q.id} className="rounded-2xl border border-stone-200 bg-white p-5" data-testid={`inquiry-${q.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium">{q.name} <span className="text-stone-500 font-normal">· {q.email}</span></p>
                      <p className="text-xs text-stone-500 mt-0.5">{q.business_name} · {q.business_type}</p>
                    </div>
                    <p className="text-xs text-stone-400">{new Date(q.created_at).toLocaleString()}</p>
                  </div>
                  <p className="text-sm text-stone-700 mt-3 leading-relaxed">{q.message}</p>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="content" className="mt-8">
            <ContentEditor refresh={refresh} contentStatus={contentStatus} />
            <div className="mt-8 space-y-8">
              <SightImagesEditor />
              <HomeFeaturesEditor />
              <BeachesExtrasEditor />
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit listing" : "New listing"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Name</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="form-name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Type</label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Rating</label>
              <Input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Description</label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Address</label>
              <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Phone</label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Email</label>
              <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Website</label>
              <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Image</label>
              <ImageUploadInput
                value={form.image_url}
                onChange={(v) => setForm({ ...form, image_url: v })}
                testId="biz-image"
                previewClassName="w-full h-32 object-cover"
              />
            </div>
            <div className="sm:col-span-2 flex gap-6 pt-2">
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /> Featured
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Switch checked={form.sponsored} onCheckedChange={(v) => setForm({ ...form, sponsored: v })} /> Sponsored
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={saveBusiness} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)]" data-testid="save-business">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}

// ============================================================
// CMS Content Editor — edit text content per section per language
// ============================================================
function ContentEditor({ refresh, contentStatus }) {
  const [section, setSection] = useState("intro");
  const [lang, setLang] = useState("en");
  const [data, setData] = useState({ title: "", subtitle: "", body: "", image_url: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genOne, setGenOne] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get(`/content/${lang}/${section}`);
      setData({ title: r.data.title || "", subtitle: r.data.subtitle || "", body: r.data.body || "", image_url: r.data.image_url || "" });
    } catch {
      setData({ title: "", subtitle: "", body: "", image_url: "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [section, lang]);

  const save = async () => {
    setSaving(true);
    try {
      await adminAxios().put(`/admin/content/${lang}/${section}`, data);
      toast.success(`Saved ${section}/${lang}`);
      refresh();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const generateOne = async () => {
    setGenOne(true);
    try {
      const r = await adminAxios().post(`/admin/content/generate`, {
        sections: [section], langs: [lang], overwrite: true,
      });
      toast.success(`Regenerated ${r.data.generated.length}, failed ${r.data.failed.length}`);
      load();
      refresh();
    } catch {
      toast.error("Regeneration failed");
    } finally {
      setGenOne(false);
    }
  };

  const exists = contentStatus.find((c) => c.section === section && c.lang === lang);

  return (
    <div className="space-y-6" data-testid="content-editor">
      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <h2 className="font-display text-2xl mb-1">Content Manager (CMS)</h2>
        <p className="text-sm text-stone-600 mb-4">Pick any page section and language, then edit the title, subtitle and body. Click Save to publish.</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Section (page)</label>
            <Select value={section} onValueChange={setSection}>
              <SelectTrigger data-testid="cms-section-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {SECTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Language</label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="cms-lang-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => <SelectItem key={l} value={l}>{l.toUpperCase()}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button variant="outline" className="w-full" onClick={generateOne} disabled={genOne} data-testid="cms-regen-btn">
              <Sparkles className="h-4 w-4 me-2" /> {genOne ? "Generating…" : "AI Regenerate"}
            </Button>
          </div>
        </div>

        {loading ? (
          <p className="text-stone-500">Loading…</p>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Title</label>
              <Input value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} data-testid="cms-title" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Subtitle</label>
              <Input value={data.subtitle} onChange={(e) => setData({ ...data, subtitle: e.target.value })} data-testid="cms-subtitle" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Hero-Bild (oberes Bild auf dieser Seite)</label>
              <ImageUploadInput
                value={data.image_url}
                onChange={(v) => setData({ ...data, image_url: v })}
                testId="cms-hero"
                previewClassName="w-full h-40 object-cover"
              />
              <p className="text-xs text-stone-500 mt-1">
                Lade ein Foto vom Handy/PC hoch oder füge eine Bild-URL ein. Gilt nur für diese Sprache — für andere Sprachen separat setzen.
              </p>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Body (use blank lines to separate paragraphs)</label>
              <Textarea
                rows={20}
                value={data.body}
                onChange={(e) => setData({ ...data, body: e.target.value })}
                data-testid="cms-body"
                className="font-mono text-sm"
              />
            </div>

            <div className="flex items-center gap-3 pt-2">
              <Button onClick={save} disabled={saving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)]" data-testid="cms-save-btn">
                {saving ? "Saving…" : "Save changes"}
              </Button>
              <Button variant="outline" onClick={load}>Discard</Button>
              {exists && <span className="text-xs text-stone-500">Last updated: {new Date(exists.updated_at).toLocaleString()}</span>}
            </div>
          </div>
        )}
      </div>

      {/* Status grid */}
      <div className="rounded-2xl border border-stone-200 bg-white p-6">
        <h3 className="font-display text-xl mb-3">All content blocks ({SECTIONS.length} sections × {LANGS.length} languages = {SECTIONS.length * LANGS.length} total)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {SECTIONS.map((s) =>
            LANGS.map((l) => {
              const found = contentStatus.find((c) => c.section === s && c.lang === l);
              const active = section === s && lang === l;
              return (
                <button
                  key={`${s}-${l}`}
                  onClick={() => { setSection(s); setLang(l); }}
                  className={`rounded-lg border p-2 text-xs text-start transition ${
                    active ? "border-[var(--brand-primary)] bg-blue-50" :
                    found ? "border-emerald-200 bg-emerald-50 hover:bg-emerald-100" :
                    "border-stone-200 bg-white hover:bg-stone-50"
                  }`}
                  data-testid={`cms-tile-${s}-${l}`}
                >
                  <p className="font-medium">{s}</p>
                  <p className="text-stone-500">{l.toUpperCase()} {found ? "✓" : "—"}</p>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Generic Image Collection Editor — used for SightsPage highlights (6),
// HomePage feature cards (4) and BeachesPage extras (2).
// Backed by the /api/images/{key} endpoint.
// ============================================================
function ImageCollectionEditor({ collectionKey, title, description, labels, columns = 2 }) {
  const count = labels.length;
  const [images, setImages] = useState(Array(count).fill(""));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminAxios().get(`/images/${collectionKey}`);
      const arr = r?.data?.images;
      if (Array.isArray(arr) && arr.length === count) setImages(arr);
    } catch {
      toast.error(`Konnte ${title} nicht laden`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [collectionKey]);

  const save = async () => {
    if (images.some((u) => !u || !u.trim())) {
      toast.error(`Alle ${count} Bilder müssen gesetzt sein`);
      return;
    }
    setSaving(true);
    try {
      await adminAxios().put(`/admin/images/${collectionKey}`, { images });
      toast.success(`${title} gespeichert`);
    } catch {
      toast.error("Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const update = (i, v) => {
    const next = [...images];
    next[i] = v;
    setImages(next);
  };

  const gridCols = columns === 1 ? "grid-cols-1" : columns === 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1 md:grid-cols-3";

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-6" data-testid={`collection-${collectionKey}`}>
      <h2 className="font-display text-2xl mb-1">{title}</h2>
      {description && <p className="text-sm text-stone-600 mb-4">{description}</p>}

      {loading ? (
        <p className="text-stone-500">Lädt…</p>
      ) : (
        <div className={`grid ${gridCols} gap-5`}>
          {images.map((url, i) => (
            <div key={i} className="rounded-xl border border-stone-200 p-3" data-testid={`${collectionKey}-${i}`}>
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">{labels[i]}</label>
              <div className="mt-2">
                <ImageUploadInput
                  value={url}
                  onChange={(v) => update(i, v)}
                  testId={`${collectionKey}-${i}`}
                  previewClassName="w-full h-32 object-cover"
                />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 pt-5">
        <Button
          onClick={save}
          disabled={saving || loading}
          className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)]"
          data-testid={`save-${collectionKey}`}
        >
          {saving ? "Speichert…" : "Speichern"}
        </Button>
        <Button variant="outline" onClick={load} disabled={loading}>Zurücksetzen</Button>
      </div>
    </div>
  );
}

function SightImagesEditor() {
  return (
    <ImageCollectionEditor
      collectionKey="sight_images"
      title="Sehenswürdigkeiten · Highlight-Bilder (6)"
      description='Diese 6 Bilder erscheinen auf der Seite „Sehenswürdigkeiten" (gleich für alle Sprachen). Foto hochladen oder URL einfügen.'
      labels={[
        "1 · Ottoman Fortress / Osmanische Festung",
        "2 · Old Town / Altstadt",
        "3 · Al-Farran Mosque / Al-Farran Moschee",
        "4 · British Phosphate Buildings",
        "5 · Wadi Hammamat Petroglyphs",
        "6 · Old Harbor / Alter Hafen",
      ]}
    />
  );
}

function HomeFeaturesEditor() {
  return (
    <ImageCollectionEditor
      collectionKey="home_features"
      title="Startseite · 4 Kachel-Bilder"
      description="Die 4 Bilder der Kategorie-Kacheln auf der Startseite (History, Beaches, Diving, Sights)."
      labels={[
        "1 · History / Osmanische Festung (Kachel History)",
        "2 · Beaches / Strand (Kachel Beaches)",
        "3 · Diving / Riff (Kachel Diving)",
        "4 · Sights / Altstadt (Kachel Sights)",
      ]}
    />
  );
}

function BeachesExtrasEditor() {
  return (
    <ImageCollectionEditor
      collectionKey="beaches_extras"
      title="Beaches-Seite · 2 Zusatzbilder"
      description="Zusätzliche Bilder auf der Beaches & Diving-Seite (Sand, Koralle)."
      labels={[
        "1 · Sand / Strand",
        "2 · Reef / Koralle",
      ]}
    />
  );
}
